import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  Typography,
  Button,
  Box,
  Tooltip,
  Autocomplete,
  IconButton,
  Breadcrumbs,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "../../../layout/Layout";
import {
  materialReceiptAPI,
  vendorAPI,
  projectMasterAPI,
  manageCompaniesAPI,
  statusMasterAPI,
  handleHrMasterAPI,
  termsAndConditionsAPI,
} from "../../../config";

const GST_RATES = [0, 5, 12, 18, 40];

function ProformaOrderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  const [loading, setLoading] = useState(false);
  const [proformaNo, setProformaNo] = useState("");
  const [proformaDate, setProformaDate] = useState("");

  // Dropdowns lists
  const [companyList, setCompanyList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [classifyList, setClassifyList] = useState([]);
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [termsList, setTermsList] = useState([]);
  const [siteEngineers, setSiteEngineers] = useState([]);
  const [unitsList, setUnitsList] = useState([]);

  // Form Fields
  const [companyId, setCompanyId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [orderType, setOrderType] = useState("1");
  const [termsOfPayment, setTermsOfPayment] = useState("");
  const [dispatchThrough, setDispatchThrough] = useState("");
  const [quotationNo, setQuotationNo] = useState("");
  const [packingCharge, setPackingCharge] = useState(0);
  const [purchaseClassify, setPurchaseClassify] = useState("");
  const [siteEngId, setSiteEngId] = useState("");
  const [siteEngContact, setSiteEngContact] = useState("");
  const [validTo, setValidTo] = useState("");
  const [deliveryScope, setDeliveryScope] = useState("");
  const [termsHeading, setTermsHeading] = useState("");
  const [terms, setTerms] = useState("");
  const [status, setStatus] = useState("Draft");

  // Address Details
  const [vendorDetails, setVendorDetails] = useState({});
  const [projectDetails, setProjectDetails] = useState({});

  // Transportation
  const [transportAmount, setTransportAmount] = useState(0);
  const [transportGst, setTransportGst] = useState(0);
  const [transportCharge, setTransportCharge] = useState(0);

  // Line items
  const [items, setItems] = useState([]);

  // Totals
  const [totalWithoutGst, setTotalWithoutGst] = useState(0);
  const [totalCgst, setTotalCgst] = useState(0);
  const [totalSgst, setTotalSgst] = useState(0);
  const [totalIgst, setTotalIgst] = useState(0);
  const [totalWithGst, setTotalWithGst] = useState(0);

  const fetchVendorDetails = (vId) => {
    if (!vId) return;
    axios.post(vendorAPI, {
      intType: 7,
      intvendorAutoID: vId,
      intLimit: 1
    }).then((res) => {
      if (res.data.success && res.data.result.length > 0) {
        setVendorDetails(res.data.result[0]);
      }
    });
  };

  const fetchProjectDetails = (pId) => {
    if (!pId) return;
    axios.post(projectMasterAPI, {
      intType: 2,
      intProjectID: pId,
      intLimit: 1
    }).then((res) => {
      if (res.data.success && res.data.result.length > 0) {
        setProjectDetails(res.data.result[0]);
      }
    });
  };

  // Fetch initial dropdown metadata
  useEffect(() => {
    // Fetch companies
    axios.post(manageCompaniesAPI, { intType: 5 }).then((res) => {
      if (res.data.success) setCompanyList(res.data.result);
    });

    // Fetch vendors
    axios.post(vendorAPI, { intType: 3 }).then((res) => {
      if (res.data.success) setVendorList(res.data.result);
    });

    // Fetch projects
    axios.post(projectMasterAPI, { intType: 6 }).then((res) => {
      if (res.data.success) setProjectList(res.data.result);
    });

    // Fetch classification / terms codes from statusMaster
    axios.post(statusMasterAPI, { intType: 2, intParentId: 68 }).then((res) => {
      if (res.data.success) setClassifyList(res.data.result);
    });
    axios.post(statusMasterAPI, { intType: 2, intParentId: 42 }).then((res) => {
      if (res.data.success) setPaymentTermsList(res.data.result);
    });
    axios.post(termsAndConditionsAPI, { intType: 4 }).then((res) => {
      if (res.data.success) setTermsList(res.data.result);
    });

    // Fetch units list
    axios.post(statusMasterAPI, { intType: 2, intParentId: 88 }).then((res) => {
      if (res.data.success) setUnitsList(res.data.result);
    });

    // Fetch site engineers
    axios.post(handleHrMasterAPI, { intType: 12 }).then((res) => {
      if (res.data.success) setSiteEngineers(res.data.result);
    });
  }, []);

  // Fetch Proforma Order Details
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    axios({
      method: "POST",
      url: materialReceiptAPI,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userData.token,
      },
      data: {
        intType: 2,
        proformaId: id,
      },
    })
      .then((res) => {
        if (res.data.success && res.data.result.length > 0) {
          const detail = res.data.result[0];
          const lineItems = res.data.result[1] || [];

          setProformaNo(detail.proforma_no);
          setProformaDate(detail.proforma_date ? detail.proforma_date.split("T")[0] : "");
          setCompanyId(detail.company_name_id || "");
          setVendorId(detail.vendor_id || "");
          fetchVendorDetails(detail.vendor_id);
          setProjectId(detail.project_id || "");
          fetchProjectDetails(detail.project_id);
          setOrderType(detail.order_type || "1");
          setTermsOfPayment(detail.terms_of_payment || "");
          setDispatchThrough(detail.dispatch_through || "");
          setQuotationNo(detail.quotation_no || "");
          setPackingCharge(parseFloat(detail.packing_charge || 0));
          setPurchaseClassify(detail.purchase_classify || "");
          setSiteEngId(detail.site_eng_id || "");
          setSiteEngContact(detail.site_eng_contact || "");
          setValidTo(detail.valid_to ? detail.valid_to.split("T")[0] : "");
          setDeliveryScope(detail.delivery_scope || "");
          setTermsHeading(detail.terms_heading || "");
          setTerms(detail.terms || "");
          setStatus(detail.status || "Draft");

          setTransportAmount(parseFloat(detail.transport_amount || 0));
          setTransportGst(parseFloat(detail.transport_gst || 0));
          setTransportCharge(parseFloat(detail.transport_charge || 0));

          // Map items
          setItems(
            lineItems.map((item) => {
              const cgst = parseFloat(item.cgst || 0);
              const sgst = parseFloat(item.sgst || 0);
              const igst = parseFloat(item.igst || 0);
              const totalAmount = parseFloat(item.total || 0);
              const rate = parseFloat(item.rate || 0);
              const quantity = parseFloat(item.quantity || 0);
              const rawSubtotal = rate * quantity;
              
              const isWithGST = (cgst === 0 && sgst === 0 && igst === 0) || 
                                (Math.abs(totalAmount - (rawSubtotal + cgst + sgst + igst)) > 0.1);
                                
              const intLineItemTotalWithoutGST = isWithGST ? (totalAmount - (cgst + sgst + igst)) : rawSubtotal;

              return {
                id: item.id,
                item_name: item.item_name || "",
                item_description: item.item_description || "",
                hsn_code: item.hsn_code || "",
                quantity: quantity,
                unit: item.unit || "",
                rate: rate,
                discount: parseFloat(item.discount || 0),
                txtDiscountType: "percentage", // default mapping
                gst_percentage: parseFloat(item.gst_percentage || 0),
                blnWithGST: isWithGST,
                cgst: cgst,
                sgst: sgst,
                igst: igst,
                intLineItemTotalWithoutGST: intLineItemTotalWithoutGST,
                total: totalAmount,
              };
            })
          );
        }
      })
      .catch((err) => {
        console.error("Failed to load Material Receipt details:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, userData.token]);

  // Handle updates to Site Engineer
  const handleSiteEngChange = (engId) => {
    setSiteEngId(engId);
    const eng = siteEngineers.find((e) => e.id === engId);
    if (eng) {
      setSiteEngContact(eng.phone_no || "");
    }
  };

  const handleVendorChange = (vId) => {
    setVendorId(vId);
    fetchVendorDetails(vId);
  };

  const handleProjectChange = (pId) => {
    setProjectId(pId);
    fetchProjectDetails(pId);
  };

  // Recalculate totals dynamically
  useEffect(() => {
    let subtotal = 0;
    let cgstSum = 0;
    let sgstSum = 0;
    let igstSum = 0;
    let totalVal = 0;

    items.forEach((item) => {
      subtotal += parseFloat(item.intLineItemTotalWithoutGST || 0);
      cgstSum += parseFloat(item.cgst || 0);
      sgstSum += parseFloat(item.sgst || 0);
      igstSum += parseFloat(item.igst || 0);
      totalVal += parseFloat(item.total || 0);
    });

    // Transport calculation
    const transAmt = parseFloat(transportAmount) || 0;
    const transGstVal = transAmt * ((parseFloat(transportGst) || 0) / 100);
    const calculatedTransCharge = transAmt + transGstVal;
    
    setTransportCharge(calculatedTransCharge);

    if (igstSum > 0) {
      igstSum += transGstVal;
    } else {
      cgstSum += transGstVal / 2;
      sgstSum += transGstVal / 2;
    }

    setTotalWithoutGst(subtotal);
    setTotalCgst(cgstSum);
    setTotalSgst(sgstSum);
    setTotalIgst(igstSum);
    
    const finalTotal = totalVal + calculatedTransCharge;
    setTotalWithGst(finalTotal);
  }, [items, transportAmount, transportGst, vendorDetails.state]);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        item_name: "",
        item_description: "",
        hsn_code: "",
        quantity: 1,
        unit: "",
        rate: 0,
        discount: 0,
        txtDiscountType: "percentage",
        gst_percentage: 18,
        blnWithGST: false,
        cgst: 0,
        sgst: 0,
        igst: 0,
        intLineItemTotalWithoutGST: 0,
        total: 0,
      },
    ]);
  };

  const handleRemoveItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleItemChange = (e, index) => {
    const { name, value } = e.target;
    const list = [...items];

    list[index][name] = value;

    const withGST = list[index]["blnWithGST"] === true;
    const gstRateFull = parseInt(list[index]["gst_percentage"] || 0);

    let baseRate = parseFloat(list[index]["rate"] || 0);
    if (withGST && gstRateFull > 0) {
      baseRate = baseRate / (1 + (gstRateFull / 100));
    }
    let qty = parseFloat(list[index]["quantity"]);
    if (isNaN(qty)) qty = 1;
    const rawSubtotal = baseRate * qty;

    const discType = list[index]["txtDiscountType"] || "";
    const discVal = parseFloat(list[index]["discount"] || 0);

    let discountedSubtotal = rawSubtotal;
    if (discType === "fixed") {
      discountedSubtotal = Math.max(0, rawSubtotal - discVal);
    } else if (discType === "percentage") {
      discountedSubtotal = Math.max(
        0,
        rawSubtotal - (rawSubtotal * discVal) / 100,
      );
    }

    list[index]["intLineItemTotalWithoutGST"] = discountedSubtotal;

    let tempGST = gstRateFull / 2;

    if (vendorDetails.state !== "TamilNadu") {
      list[index]["igst"] = (tempGST / 100) * discountedSubtotal * 2;
      list[index]["cgst"] = 0;
      list[index]["sgst"] = 0;
    } else {
      list[index]["cgst"] = (tempGST / 100) * discountedSubtotal;
      list[index]["sgst"] = (tempGST / 100) * discountedSubtotal;
      list[index]["igst"] = 0;
    }

    if (vendorDetails.state !== "TamilNadu") {
      list[index]["total"] = discountedSubtotal + list[index]["igst"];
    } else {
      list[index]["total"] = discountedSubtotal + list[index]["cgst"] + list[index]["sgst"];
    }

    setItems(list);
  };

  const handleSave = () => {
    setLoading(true);
    axios({
      method: "POST",
      url: materialReceiptAPI,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + userData.token,
      },
      data: {
        intType: 5,
        proformaId: id,
        proforma_no: proformaNo,
        vendor_id: vendorId,
        order_type: orderType,
        company_name_id: companyId,
        project_id: projectId,
        terms_of_payment: termsOfPayment,
        dispatch_through: dispatchThrough,
        quotation_no: quotationNo,
        packing_charge: 0,
        purchase_classify: purchaseClassify,
        site_eng_id: siteEngId,
        site_eng_contact: siteEngContact,
        valid_to: validTo,
        delivery_scope: deliveryScope,
        terms_heading: termsHeading,
        terms: terms,
        transport_amount: transportAmount,
        transport_gst: transportGst,
        transport_charge: transportCharge,
        status: status,
        items: items.map(item => ({
          ...item,
          gst_percentage: item.gst_percentage
        })),
      },
    })
      .then((res) => {
        if (res.data.success) {
          alert(res.data.message);
          navigate("/material-receipt/list");
        }
      })
      .catch((err) => {
        console.error("Save failed:", err);
        alert("Failed to save Material Receipt.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left" variant="h5" fontWeight="bold">
              Edit Material Receipt ({proformaNo})
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Link underline="hover" color="inherit" href="/material-receipt/list">
                Material Receipt List
              </Link>
              <Typography color="text.primary">Edit</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs={12} lg={6} md={6} style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </Button>
          </Grid>
        </Grid>

        <div className="empty-space-40"></div>

        <Grid className="purchaseorder">
          <Grid container className="popading" spacing={2}>
            {/* Order Type */}
            <Grid item className="form-lable po_lebleterm" xs={12} lg={3} md={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  options={[
                    { id: "1", name: "Purchase Order" },
                    { id: "2", name: "Work Order" },
                  ]}
                  getOptionLabel={(option) => option?.name || ""}
                  value={
                    [
                      { id: "1", name: "Purchase Order" },
                      { id: "2", name: "Work Order" },
                    ].find((o) => String(o.id) === String(orderType)) || null
                  }
                  onChange={(event, newValue) => {
                    setOrderType(newValue?.id || "1");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Order Type"
                      placeholder="Search Order Type"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {/* Vendor Name */}
            <Grid item className="form-lable po_lebleterm" xs={12} lg={3} md={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  options={vendorList}
                  getOptionLabel={(option) => option?.name || ""}
                  value={vendorList.find((v) => String(v.id) === String(vendorId)) || null}
                  onChange={(event, newValue) => {
                    handleVendorChange(newValue?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendor Name"
                      placeholder="Search Vendor"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {/* Company Name */}
            <Grid item className="form-lable po_lebleterm" xs={12} lg={3} md={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  options={companyList}
                  getOptionLabel={(option) => option?.company_name || ""}
                  value={companyList.find((c) => String(c.id) === String(companyId)) || null}
                  onChange={(event, newValue) => {
                    setCompanyId(newValue?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Company Name"
                      placeholder="Search Company"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            {/* Select Project */}
            <Grid item className="form-lable po_lebleterm" xs={12} lg={3} md={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  options={projectList}
                  getOptionLabel={(option) => option?.name || ""}
                  value={projectList.find((p) => String(p.id) === String(projectId)) || null}
                  onChange={(event, newValue) => {
                    handleProjectChange(newValue?.id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Project"
                      placeholder="Search Project"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Grid container className="pomargin table_po">
            <Grid container spacing={2} className="po_leble">
              {/* Bill To */}
              <Grid item xs={12} lg={4} md={4} className="form-lable">
                <Grid className="info_details">
                  <Typography variant="h6" sx={{ fontSize: "20px" }}>
                    Bill to
                  </Typography>
                  {vendorDetails && vendorDetails.name && (
                    <>
                      <Typography className="companyna">
                        {vendorDetails.name}
                      </Typography>
                      <Typography>{vendorDetails.mobile_no}</Typography>
                      <Typography>
                        {vendorDetails.street_address}, {vendorDetails.city}
                      </Typography>
                      <Typography>
                        {vendorDetails.pincode} {vendorDetails.state}
                      </Typography>
                      <Typography>India</Typography>
                      <Typography>GSTIN {vendorDetails.gst}</Typography>
                    </>
                  )}
                </Grid>
              </Grid>

              {/* Place of Supply */}
              <Grid item xs={12} lg={4} md={4} className="form-lable">
                <Grid className="info_details">
                  <Typography variant="h6" sx={{ fontSize: "20px" }}>
                    Place of Supply:
                  </Typography>
                  {projectDetails && projectDetails.name && (
                    <>
                      <Typography className="companyna">
                        {projectDetails.project_address}
                      </Typography>
                      <Typography>{projectDetails.city}</Typography>
                      <Typography>
                        {projectDetails.pincode} {projectDetails.state}
                      </Typography>
                      <Typography>India</Typography>
                    </>
                  )}
                </Grid>
              </Grid>

              {/* Other Details Right Card */}
              <Grid item xs={12} lg={4} md={4} sx={{ border: "1px solid #000", mt: 2 }}>
                {/* Supplier Ref */}
                <Grid container spacing={1} alignItems="center" className="po_leble">
                  <Grid item xs={12}>
                    <Typography>Supplier Ref :</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={quotationNo}
                      onChange={(e) => setQuotationNo(e.target.value)}
                    />
                  </Grid>
                </Grid>

                {/* Classification */}
                <Grid container spacing={1} alignItems="center" className="po_leble">
                  <Grid item xs={12}>
                    <Typography>
                      {orderType === "1" ? "PO classification" : "WO classification"}
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={purchaseClassify ? String(purchaseClassify) : ""}
                        onChange={(e) => setPurchaseClassify(e.target.value)}
                      >
                        {classifyList.map((row, index) => (
                          <MenuItem value={String(row.id)} key={index}>
                            {row.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Site Engineer */}
                <Grid container spacing={1} alignItems="center" className="po_leble">
                  <Grid item xs={12}>
                    <Typography>Site Engineer:</Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={siteEngId ? String(siteEngId) : ""}
                        onChange={(e) => handleSiteEngChange(e.target.value)}
                      >
                        {siteEngineers.map((row, index) => (
                          <MenuItem value={String(row.id)} key={index}>
                            {row.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Contact */}
                <Grid container spacing={1} alignItems="center" className="po_leble">
                  <Grid item xs={12}>
                    <Typography>Contact:</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={siteEngContact}
                      onChange={(e) => setSiteEngContact(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Table Section */}
            <Grid item className="form-lable" xs={12} lg={12} md={12} sx={{ mt: 3 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}><Typography>S. No.</Typography></th>
                    <th style={{ width: "28%" }}><Typography>Description</Typography></th>
                    <th style={{ width: "10%" }}><Typography>UNO</Typography></th>
                    <th style={{ width: "12%" }}><Typography>Rate</Typography></th>
                    <th style={{ width: "15%" }}><Typography>Discount</Typography></th>
                    <th style={{ width: "10%" }}><Typography>GST</Typography></th>
                    <th style={{ width: "15%" }}><Typography>Total Amount Without GST</Typography></th>
                    <th style={{ width: "5%" }} className="add_button">
                      <Tooltip title="Add New" arrow>
                        <Button
                          variant="outlined"
                          onClick={handleAddItem}
                          startIcon={<AddIcon />}
                          color="success"
                        />
                      </Tooltip>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((singleService, index) => (
                    <tr className="table_padding" key={index}>
                      <td className="text-center">
                        <Typography>{index + 1}</Typography>
                      </td>
                      <td className="po_leble">
                        <TextField
                          name="item_name"
                          type="text"
                          value={singleService.item_name}
                          onChange={(e) => handleItemChange(e, index)}
                          required
                          fullWidth
                          multiline
                          style={{ width: "100%" }}
                        />
                      </td>
                      <td className="po_leble">
                        <Select
                          name="unit"
                          value={singleService.unit}
                          onChange={(e) => handleItemChange(e, index)}
                          style={{ width: "100%", marginBottom: "4px" }}
                        >
                          {unitsList.map((row, index) => (
                            <MenuItem value={row.id} key={index}>
                              {row.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <TextField
                          name="quantity"
                          type="number"
                          value={singleService.quantity}
                          onChange={(e) => handleItemChange(e, index)}
                          style={{ width: "100%" }}
                        />
                      </td>
                      <td className="po_leble">
                        <RadioGroup
                          row
                          name="blnWithGST"
                          value={singleService.blnWithGST === true ? "with" : "without"}
                          onChange={(e) => {
                            const synthEvent = {
                              target: {
                                name: "blnWithGST",
                                value: e.target.value === "with",
                              },
                            };
                            handleItemChange(synthEvent, index);
                          }}
                          style={{ marginBottom: "4px" }}
                        >
                          <FormControlLabel
                            value="without"
                            control={<Radio size="small" style={{ padding: 4 }} />}
                            label={<span style={{ fontSize: '11px' }}>Without GST</span>}
                            style={{ marginRight: 8 }}
                          />
                          <FormControlLabel
                            value="with"
                            control={<Radio size="small" style={{ padding: 4 }} />}
                            label={<span style={{ fontSize: '11px' }}>With GST</span>}
                          />
                        </RadioGroup>
                        <TextField
                          name="rate"
                          type="number"
                          value={singleService.rate}
                          onChange={(e) => handleItemChange(e, index)}
                          style={{ width: "100%" }}
                        />
                      </td>
                      <td className="po_leble">
                        <FormControl fullWidth size="small" style={{ marginBottom: 4 }}>
                          <Select
                            displayEmpty
                            name="txtDiscountType"
                            value={singleService.txtDiscountType || ""}
                            onChange={(e) => handleItemChange(e, index)}
                          >
                            <MenuItem value=""><em>None</em></MenuItem>
                            <MenuItem value="percentage">Percentage (%)</MenuItem>
                            <MenuItem value="fixed">(Rs.) Fixed</MenuItem>
                          </Select>
                        </FormControl>
                        {singleService.txtDiscountType && (
                          <TextField
                            name="discount"
                            type="number"
                            size="small"
                            placeholder={singleService.txtDiscountType === "percentage" ? "0–100" : "Rs. Amount"}
                            value={singleService.discount}
                            onChange={(e) => handleItemChange(e, index)}
                            style={{ width: "100%" }}
                          />
                        )}
                      </td>
                      <td className="po_leble">
                        <FormControl fullWidth>
                          <Select
                            name="gst_percentage"
                            value={singleService.gst_percentage || ""}
                            onChange={(e) => handleItemChange(e, index)}
                            style={{ width: "100%" }}
                          >
                            {GST_RATES.map((r) => (
                              <MenuItem value={r} key={r}>
                                GST {r}%
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </td>
                      <td className="po_leble">
                        <Typography textAlign="right">
                          {parseFloat(singleService.intLineItemTotalWithoutGST || 0).toFixed(2)}
                        </Typography>
                      </td>
                      <td className="cancel_button">
                        {items.length !== 1 && (
                          <IconButton color="error" onClick={() => handleRemoveItem(index)}>
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  ))}

                  {/* Calculations Footers */}
                  <tr>
                    <td colSpan={6} style={{ textAlign: "right", fontWeight: "bold" }}>Total amount</td>
                    <td style={{ textAlign: "right" }}>{totalWithoutGst.toFixed(2)}</td>
                    <td />
                  </tr>
                  {totalIgst > 0 ? (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "right", fontWeight: "bold" }}>IGST</td>
                      <td style={{ textAlign: "right" }}>{totalIgst.toFixed(2)}</td>
                      <td />
                    </tr>
                  ) : (
                    <>
                      <tr>
                        <td colSpan={6} style={{ textAlign: "right", fontWeight: "bold" }}>CGST</td>
                        <td style={{ textAlign: "right" }}>{totalCgst.toFixed(2)}</td>
                        <td />
                      </tr>
                      <tr>
                        <td colSpan={6} style={{ textAlign: "right", fontWeight: "bold" }}>SGST</td>
                        <td style={{ textAlign: "right" }}>{totalSgst.toFixed(2)}</td>
                        <td />
                      </tr>
                    </>
                  )}

                  {/* Transport Charges */}
                  <tr>
                    <td colSpan={4} style={{ textAlign: "right", fontWeight: "bold" }}>Transport Charges</td>
                    <td>
                      <Select
                        name="transportGst"
                        value={transportGst}
                        onChange={(e) => setTransportGst(parseFloat(e.target.value) || 0)}
                        style={{ width: "100%" }}
                      >
                        {GST_RATES.map((r) => (
                          <MenuItem value={r} key={r}>
                            GST {r}%
                          </MenuItem>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <TextField
                        type="number"
                        value={transportAmount}
                        onChange={(e) => setTransportAmount(parseFloat(e.target.value) || 0)}
                        style={{ width: "100%" }}
                      />
                    </td>
                    <td style={{ textAlign: "right" }}>{transportCharge.toFixed(2)}</td>
                    <td />
                  </tr>

                  {/* Packing Charge Removed */}

                  {/* Final Payable Total */}
                  <tr>
                    <td colSpan={6} style={{ textAlign: "right", fontWeight: "bold", fontSize: "16px" }}>Total Payable Amount (Rs.)</td>
                    <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "16px" }}>
                      {Math.round(totalWithGst).toFixed(2)} /-
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </Grid>
          </Grid>

          <div className="empty-space-20"></div>

          <Grid container spacing={2}>
            {/* Terms and Conditions Dropdown & text */}
            <Grid
              item
              xs={12}
              md={4}
              lg={4}
              textAlign="left"
              className="po_lebleterm"
            >
              <TextField
                select
                label="Terms & Conditions"
                value={termsHeading ? String(termsHeading) : ""}
                onChange={(e) => {
                  setTermsHeading(e.target.value);
                  axios.post(termsAndConditionsAPI, {
                    intType: 3,
                    intID: e.target.value,
                    intLimit: 1
                  }).then((res) => {
                    if (res.data.success && res.data.result.length > 0) {
                      setTerms(res.data.result[0].description || "");
                    }
                  });
                }}
                sx={{ my: 1, mx: 0, width: "300px" }}
              >
                {termsList.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
            >
              <TextField
                fullWidth
                multiline
                rows={5}
                label="Terms Body"
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
              />
            </Grid>

            {/* Submit button */}
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 3 }}>
              <LoadingButton
                size="large"
                variant="contained"
                color="primary"
                loading={loading}
                onClick={handleSave}
              >
                Submit
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default ProformaOrderEdit;
